import { CanvasRenderer } from '../renderers/CanvasRenderer';
import { SVGRenderer } from '../renderers/SVGRenderer';
import { getWorldBounds } from '../performance/bounds';
import { Matrix2D } from '../utils';
/** Resolve export region to world-space bounds. */
export function resolveExportBounds(app, region) {
    if (!region) {
        return { x: 0, y: 0, width: app.getSize().width, height: app.getSize().height };
    }
    if (isNode(region)) {
        return getWorldBounds(region);
    }
    return region;
}
function isNode(value) {
    return typeof value.getBounds === 'function';
}
/** Render scene to offscreen canvas for raster export. */
export function snapshotToCanvas(app, options) {
    var _a, _b;
    if (options === void 0) { options = { format: 'png' }; }
    var bounds = resolveExportBounds(app, options.region);
    var pixelRatio = (_a = options.pixelRatio) !== null && _a !== void 0 ? _a : app.getPixelRatio();
    var width = Math.max(1, Math.ceil(bounds.width));
    var height = Math.max(1, Math.ceil(bounds.height));
    var background = (_b = options.background) !== null && _b !== void 0 ? _b : app.getBackground();
    if (app.getRenderer() instanceof CanvasRenderer && !options.region) {
        var src = app.getRenderer().getElement();
        var out = document.createElement('canvas');
        out.width = width * pixelRatio;
        out.height = height * pixelRatio;
        var ctx = out.getContext('2d');
        if (ctx) {
            ctx.drawImage(src, 0, 0);
        }
        return { canvas: out, width: width, height: height, pixelRatio: pixelRatio };
    }
    if (app.getRenderer() instanceof CanvasRenderer && options.region) {
        var src = app.getRenderer().getElement();
        var out = document.createElement('canvas');
        out.width = width * pixelRatio;
        out.height = height * pixelRatio;
        var ctx = out.getContext('2d');
        if (ctx) {
            var sx = bounds.x * pixelRatio;
            var sy = bounds.y * pixelRatio;
            var sw = width * pixelRatio;
            var sh = height * pixelRatio;
            ctx.drawImage(src, sx, sy, sw, sh, 0, 0, sw, sh);
        }
        return { canvas: out, width: width, height: height, pixelRatio: pixelRatio };
    }
    var container = document.createElement('div');
    var renderer = new CanvasRenderer();
    renderer.init(container, {
        width: width,
        height: height,
        pixelRatio: pixelRatio,
        background: background,
        highContrast: false,
    });
    var matrix = buildExportMatrix(app, bounds, width, height);
    renderer.render(app.stage, matrix);
    var canvas = renderer.getElement();
    renderer.destroy();
    return { canvas: canvas, width: width, height: height, pixelRatio: pixelRatio };
}
/** Camera matrix adjusted to frame export bounds. */
function buildExportMatrix(app, bounds, width, height) {
    var base = app.camera.getMatrix();
    var m = new Matrix2D();
    m.translate(-bounds.x, -bounds.y);
    var combined = new Matrix2D();
    combined.multiply(base);
    combined.multiply(m);
    if (bounds.width !== width || bounds.height !== height) {
        var sx = width / bounds.width;
        var sy = height / bounds.height;
        var scale = new Matrix2D();
        scale.scale(sx, sy);
        combined.multiply(scale);
    }
    return combined;
}
/** Serialize standalone SVG XML document. */
export function exportSvgDocument(app, options) {
    var _a;
    if (options === void 0) { options = { format: 'svg' }; }
    var bounds = resolveExportBounds(app, options.region);
    var background = (_a = options.background) !== null && _a !== void 0 ? _a : app.getBackground();
    if (app.getRenderer() instanceof SVGRenderer && !options.region) {
        return wrapSvgDocument(serializeSvgElement(app.getRenderer().getElement()), bounds.width, bounds.height);
    }
    var container = document.createElement('div');
    var renderer = new SVGRenderer();
    renderer.init(container, {
        width: bounds.width,
        height: bounds.height,
        pixelRatio: 1,
        background: background,
        highContrast: false,
    });
    var matrix = buildExportMatrix(app, bounds, bounds.width, bounds.height);
    renderer.render(app.stage, matrix);
    var xml = wrapSvgDocument(serializeSvgElement(renderer.getElement()), bounds.width, bounds.height);
    renderer.destroy();
    return xml;
}
function serializeSvgElement(svg) {
    return new XMLSerializer().serializeToString(svg);
}
function wrapSvgDocument(inner, width, height) {
    var hasDeclaration = inner.trimStart().startsWith('<?xml');
    var doc = hasDeclaration
        ? inner
        : "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n".concat(inner);
    if (!doc.includes('xmlns=')) {
        return doc.replace('<svg', "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"".concat(width, "\" height=\"").concat(height, "\""));
    }
    return doc;
}
